import {
  questionQualityFieldSiblings,
  questionQualityVisibleHomes,
  selectHomeRecommendations,
} from "./confidence"
import { isPerformanceCheckId } from "./performance-suggestions"
import { toSelectionPlain } from "./selection-ask"
import {
  qualitySuggestionLines,
  type QualitySuggestionLine,
} from "./suggestion-lines"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualitySnapshot,
} from "./types"
import { VARIATION_BOTH_CHECK_ID } from "./variation-ask"

export type HeroSelectionScope = "stem" | "distractors" | "set" | "other"

function normalizeHeroText(value: string | undefined): string {
  return toSelectionPlain(value ?? "")
    .replace(/[.?!]+$/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
}

/** Stem variants that mean the same item to an author. */
export function stemFingerprint(value: string | undefined): string {
  return normalizeHeroText(value).replace(
    /^(identify which|identify|which|what|name)\s+/,
    "",
  )
}

function alternateStemText(alternate: QuestionQualityAlternate): string {
  return alternate.patch?.leadIn ?? alternate.text
}

export function isNearDuplicateStem(
  left: string | undefined,
  right: string | undefined,
): boolean {
  const a = stemFingerprint(left)
  const b = stemFingerprint(right)
  if (!a || !b) return false
  return a === b
}

export function heroSelectionScope(
  alternate: QuestionQualityAlternate,
): HeroSelectionScope {
  const patch = alternate.patch
  if (patch?.leadIn && patch?.optionTexts?.length) return "set"
  if (patch?.optionTexts?.length) return "distractors"
  if (patch?.optionText && !patch?.leadIn) return "distractors"
  if (patch?.leadIn) return "stem"
  return "other"
}

export function heroAlternateActionLabel(
  alternate: QuestionQualityAlternate,
): string {
  const scope = heroSelectionScope(alternate)
  if (scope === "set") return "Use this set"
  if (scope === "distractors") {
    const count = alternate.patch?.optionTexts?.length ?? 0
    if (count > 1) return "Use these distractors"
    return "Use this distractor"
  }
  if (scope === "stem") return "Use this stem"
  return alternate.actionLabel
}

export function heroTryAnotherLabel(scope: HeroSelectionScope): string {
  switch (scope) {
    case "stem":
      return "Different stem"
    case "distractors":
      return "Different distractors"
    case "set":
      return "Different set"
    default:
      return "Try another"
  }
}

export function heroSimilarAsk(scope: HeroSelectionScope): string {
  switch (scope) {
    case "stem":
      return "Suggest a similar stem"
    case "distractors":
      return "Suggest similar distractors"
    case "set":
      return "Suggest a similar set"
    default:
      return "Suggest something similar"
  }
}

/** Drop alternates that repeat what is already on the canvas. */
export function filterHeroAlternates(
  alternates: QuestionQualityAlternate[],
  check: QuestionQualityCheck,
  snapshot: QuestionQualitySnapshot,
): QuestionQualityAlternate[] {
  const currentStem = stemFingerprint(snapshot.leadIn)
  const seenStem = new Set<string>()
  return alternates.filter((alternate) => {
    const stemText = alternateStemText(alternate)
    const fingerprint = stemFingerprint(stemText)
    if (
      (check.field === "leadIn" || check.id.startsWith("variation-")) &&
      fingerprint &&
      (fingerprint === currentStem ||
        isNearDuplicateStem(stemText, snapshot.leadIn))
    ) {
      return false
    }
    if (
      heroSelectionScope(alternate) === "stem" &&
      fingerprint &&
      seenStem.has(fingerprint)
    ) {
      return false
    }
    if (heroSelectionScope(alternate) === "stem" && fingerprint) {
      seenStem.add(fingerprint)
    }
    return true
  })
}

/** One stem and one distractor path when both are available. */
export function pickHeroAlternates(
  check: QuestionQualityCheck,
  alternates: QuestionQualityAlternate[],
  snapshot: QuestionQualitySnapshot,
): QuestionQualityAlternate[] {
  const filtered = filterHeroAlternates(alternates, check, snapshot)
  if (check.id !== VARIATION_BOTH_CHECK_ID) {
    return filtered.slice(0, 2)
  }

  const stems: QuestionQualityAlternate[] = []
  const distractors: QuestionQualityAlternate[] = []
  const sets: QuestionQualityAlternate[] = []

  for (const alternate of filtered) {
    const scope = heroSelectionScope(alternate)
    if (scope === "set") sets.push(alternate)
    else if (scope === "distractors") distractors.push(alternate)
    else if (scope === "stem") {
      const nextStem = alternateStemText(alternate)
      if (
        !stems.some((item) =>
          isNearDuplicateStem(alternateStemText(item), nextStem),
        )
      ) {
        stems.push(alternate)
      }
    }
  }

  const picked: QuestionQualityAlternate[] = []
  if (stems[0]) picked.push(stems[0])
  if (distractors[0]) picked.push(distractors[0])
  else if (sets[0]) picked.push(sets[0])
  if (picked.length === 0) return filtered.slice(0, 1)
  return picked.slice(0, 2)
}

/** One ranked apply path per line item for the Ask Leo set pager. */
export function pickHeroCatalogAlternate(
  check: QuestionQualityCheck,
  alternates: QuestionQualityAlternate[],
  snapshot: QuestionQualitySnapshot,
): QuestionQualityAlternate | null {
  return pickHeroAlternates(check, alternates, snapshot)[0] ?? null
}

/** One Ask Leo set row per line item; every recommendation for that row stays on one page. */
export type LeoHeroSetLineItem = {
  home: QuestionQualityCheck
  lineId: string
  alternates: QuestionQualityAlternate[]
}

export function leoHeroSetLineItems(
  checks: QuestionQualityCheck[],
  snapshot: QuestionQualitySnapshot,
): LeoHeroSetLineItem[] {
  const items: LeoHeroSetLineItem[] = []
  for (const home of questionQualityVisibleHomes(checks, "mark", snapshot)) {
    if (isPerformanceCheckId(home.id)) {
      items.push({ home, lineId: home.id, alternates: [] })
      continue
    }
    const siblings = questionQualityFieldSiblings(checks, home)
    const rawRecs = selectHomeRecommendations(siblings, checks)
    const alternate = pickHeroCatalogAlternate(home, rawRecs, snapshot)
    items.push({
      home,
      lineId: home.id,
      alternates: alternate ? [alternate] : [],
    })
  }
  return items
}

export function leoHeroFirstLineItemId(
  checks: QuestionQualityCheck[],
  snapshot: QuestionQualitySnapshot,
): string | null {
  return leoHeroSetLineItems(checks, snapshot)[0]?.lineId ?? null
}

/** False while the Ask Leo set pager still has recommendation rows open. */
export function leoHeroTrailHasRemaining(
  checks: QuestionQualityCheck[],
  snapshot: QuestionQualitySnapshot,
): boolean {
  return leoHeroSetLineItems(checks, snapshot).length > 0
}

export function leoHeroLineItemForFocus(
  checks: QuestionQualityCheck[],
  snapshot: QuestionQualitySnapshot,
  focusCheckId: string | null,
): LeoHeroSetLineItem | null {
  const items = leoHeroSetLineItems(checks, snapshot)
  if (items.length === 0) return null
  if (!focusCheckId) return items[0] ?? null
  const exact = items.find((item) => item.lineId === focusCheckId)
  if (exact) return exact
  const homeMatch = items.find((item) => item.home.id === focusCheckId)
  return homeMatch ?? items[0] ?? null
}

export function nextLeoHeroLineItemId(
  checks: QuestionQualityCheck[],
  snapshot: QuestionQualitySnapshot,
  focusCheckId: string | null,
  appliedHomeId: string,
  appliedAlternateId: string,
): string | null {
  const items = leoHeroSetLineItems(checks, snapshot)
  const currentIndex = items.findIndex((item) => {
    if (focusCheckId && item.lineId === focusCheckId) return true
    return item.home.id === appliedHomeId
  })
  if (currentIndex >= 0 && currentIndex < items.length - 1) {
    return items[currentIndex + 1]!.lineId
  }
  return null
}

export function heroApplyUserLabel(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): string {
  return heroAlternateActionLabel(alternate) || alternate.actionLabel || check.actionLabel
}

export function heroFindingIntro(check: QuestionQualityCheck): string {
  return `${check.title}. ${check.mechanism}`
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>()
  const rows: string[] = []
  for (const value of values) {
    const trimmed = value.trim()
    if (!trimmed || seen.has(trimmed.toLowerCase())) continue
    seen.add(trimmed.toLowerCase())
    rows.push(trimmed)
  }
  return rows
}

function joinNatural(values: string[]): string {
  const rows = uniqueStrings(values)
  if (rows.length === 0) return ""
  if (rows.length === 1) return rows[0]
  if (rows.length === 2) return `${rows[0]} and ${rows[1]}`
  return `${rows.slice(0, -1).join(", ")}, and ${rows.at(-1)}`
}

export function heroSelectedStripLabel(scope: HeroSelectionScope): string {
  switch (scope) {
    case "distractors":
      return "Options updated"
    case "stem":
      return "Question stem updated"
    case "set":
      return "Stem and options updated"
    default:
      return "Update applied"
  }
}

function formatRemovedChoices(replaced: QualitySuggestionLine[]): string {
  const removed = uniqueStrings(
    replaced.map((line) => line.replaces!).filter(Boolean),
  )
  if (removed.length === 0) return ""
  if (removed.length === 1) {
    return `${removed[0]} is off the item now.`
  }
  if (removed.length === 2) {
    return `${removed[0]} and ${removed[1]} are off the item now.`
  }
  return `${removed[0]} and ${removed[1]} are off the item now, along with other weak choices.`
}

/** Options (or stem) that changed — shown alone in the post-select strip. */
export function heroSelectedAppliedLines(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): QualitySuggestionLine[] {
  const lines = qualitySuggestionLines(check, alternate)
  const scope = heroSelectionScope(alternate)
  if (scope === "stem") {
    const stemLine = lines.find((line) => !line.replaces) ?? lines[0]
    return stemLine ? [stemLine] : []
  }
  const updated = lines.filter((line) => line.replaces || line.isNew)
  return updated.length > 0 ? updated : lines
}

/** One plain sentence under the post-select strip. Canvas holds the full preview. */
export function heroSelectedStripSummary(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): string {
  const scope = heroSelectionScope(alternate)
  const lines = qualitySuggestionLines(check, alternate)
  const replaced = lines.filter((line) => line.replaces)
  const kept = lines.filter((line) => line.isKept)

  if (scope === "distractors" && replaced.length > 0) {
    const removedLine = formatRemovedChoices(replaced)
    if (kept.length > 0) {
      return `${kept[0].text} is still the correct answer. ${removedLine}`
    }
    return removedLine
  }

  if (scope === "distractors") {
    const count = alternate.patch?.optionTexts?.length ?? lines.length
    if (count === 1) {
      return "One answer choice changed. See it on the canvas."
    }
    if (count > 1) {
      return "Answer choices changed. See them on the canvas."
    }
  }

  if (scope === "stem") {
    const text = plainStem(alternate.patch?.leadIn ?? alternate.text)
    return text
      ? "The question stem changed. Review it on the canvas."
      : "The question stem changed. Review it on the canvas."
  }

  if (scope === "set") {
    return "The stem and answer choices changed. Review them on the canvas."
  }

  return "Review the update on the canvas."
}

function plainStem(value: string | undefined): string {
  return toSelectionPlain(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
}

export function heroSelectedAck(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): string {
  const scope = heroSelectionScope(alternate)
  const lines = qualitySuggestionLines(check, alternate)
  const replaced = lines.filter((line) => line.replaces)
  const count = alternate.patch?.optionTexts?.length ?? lines.length

  if (scope === "distractors") {
    const kept = lines.filter((line) => line.isKept)
    const parts: string[] = ["Got it."]
    if (kept.length > 0) {
      parts.push(`${kept[0].text} is still the correct answer.`)
    }
    if (replaced.length >= 2) {
      const removed = joinNatural(replaced.map((line) => line.replaces!))
      parts.push(`I removed ${removed} and filled in stronger options.`)
      return parts.join(" ")
    }
    if (replaced.length === 1) {
      parts.push(
        `I removed ${replaced[0].replaces} and filled in a stronger option.`,
      )
      return parts.join(" ")
    }
    if (count === 1) {
      parts.push("One answer choice is updated on the canvas.")
    } else {
      parts.push("Answer choices are updated on the canvas.")
    }
    return parts.join(" ")
  }

  if (scope === "stem") {
    return "Got it. The question stem is updated on the canvas."
  }

  if (scope === "set") {
    return "Got it. The stem and answer choices are updated on the canvas."
  }

  return "Got it. Your update is on the canvas."
}

export function heroSelectedClosingLead(scope: HeroSelectionScope): string {
  switch (scope) {
    case "set":
      return "Want to keep shaping this set, or try another path?"
    case "distractors":
      return "Want to refine these distractors, or try something else?"
    case "stem":
      return "Want to refine this stem, or try another path?"
    default:
      return "What would you like to do next?"
  }
}

export function heroSelectedFollowUpChips(
  scope: HeroSelectionScope,
  hasNextFinding: boolean,
): string[] {
  const rows: string[] = [heroSimilarAsk(scope), heroTryAnotherLabel(scope)]
  if (hasNextFinding) rows.push("Next finding")
  return rows.slice(0, 3)
}

export function heroSelectionPreviewLines(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): QualitySuggestionLine[] {
  const lines = qualitySuggestionLines(check, alternate)
  if (lines.length > 0) return lines
  const text = alternate.text.trim()
  return text ? [{ text }] : []
}

/** One line for the post-select confirmation strip. */
export function heroSelectionSummary(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): string {
  const lines = qualitySuggestionLines(check, alternate)
  if (lines.length >= 2) {
    const parts = lines
      .filter((line) => line.replaces)
      .slice(0, 2)
      .map((line) => `${line.text} replaces ${line.replaces}`)
    if (parts.length > 0) return parts.join("; ")
  }
  if (lines.length === 1) {
    const line = lines[0]
    if (line.replaces) return `${line.text} replaces ${line.replaces}`
    return line.text
  }
  const text = alternate.text.trim()
  if (text) return text
  return alternate.actionLabel
}
